import React, { useEffect, useState } from 'react';
import { trackEnrollment } from '../lib/pixel';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) { setError('Missing session id.'); setStatus('failed'); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setRecord(data); setStatus('paid');
          trackEnrollment({ value: (data.amount_total || 0) / 100, currency: 'USD', source: 'stripe_checkout' });
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('failed'); return;
        }
      } catch (err) {
        if (attempts >= 8) { setError('Could not confirm your payment. Coach Abrie will follow up.'); setStatus('failed'); return; }
      }
      if (attempts >= 15) { setStatus('timeout'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-splash-cream">
      <div className="max-w-lg w-full bg-white rounded-3xl border border-splash-sand shadow-sm p-8 text-center" data-testid="payment-success-page">
        {status === 'polling' && (
          <>
            <Loader2 className="w-10 h-10 text-splash-teal animate-spin mx-auto mb-3" />
            <h1 className="font-display font-bold text-2xl text-splash-green">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-splash-green/70">Hold tight — Stripe usually confirms within a few seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-splash-teal mx-auto mb-3" />
            <h1 className="font-display font-bold text-3xl text-splash-green">You're in!</h1>
            <p className="mt-2 text-splash-green/80">
              Thanks, {record?.parent_name || 'friend'}. Your payment of ${Number(record?.amount || 0).toFixed(2)} for {record?.swimmers} was received.
            </p>
            <div className="mt-5 rounded-2xl bg-splash-cream border border-splash-sand p-4 text-left text-sm text-splash-green">
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-splash-teal" />
                <div>A receipt has been emailed to you, and Coach Abrie will reach out within 24 hours to lock in your schedule.</div>
              </div>
            </div>
            <Link to="/" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-5 py-2.5 text-sm font-semibold btn-transition">
              Back to home
            </Link>
          </>
        )}
        {(status === 'failed' || status === 'timeout') && (
          <>
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-splash-coral/15 grid place-items-center text-splash-coralDark font-bold">!</div>
            <h1 className="font-display font-bold text-2xl text-splash-green">
              {status === 'timeout' ? 'Still processing…' : 'Payment not confirmed'}
            </h1>
            <p className="mt-2 text-sm text-splash-green/70">{error || "If your card was charged, you'll receive a confirmation shortly. Otherwise please try again or contact Coach Abrie."}</p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <Link to="/" className="rounded-full bg-white border border-splash-sand text-splash-green px-5 py-2.5 text-sm font-semibold btn-transition hover:bg-splash-cream">Home</Link>
              <a href="mailto:abrie@littlesplashswimpro.com" className="rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-5 py-2.5 text-sm font-semibold btn-transition">Email Coach Abrie</a>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
